package co.maytes.standupbot.dashboard.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.layout.ShellGround
import co.maytes.standupbot.dashboard.features.settings.LocalSettingsNavigate
import co.maytes.standupbot.dashboard.features.settings.SettingsCatalog
import co.maytes.standupbot.dashboard.features.settings.SettingsHit
import co.maytes.standupbot.dashboard.features.settings.SettingsJump
import co.maytes.standupbot.dashboard.features.settings.SettingsSearchDialog
import co.maytes.standupbot.dashboard.features.settings.SettingsSidebar
import co.maytes.standupbot.dashboard.features.settings.settingsRegistry
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberSetupStatusStore
import co.maytes.standupbot.dashboard.styles.Layout
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

@Page("/app/settings/{tab?}")
@Composable
fun SettingsHubPage() = AuthGuard {
    val ctx = rememberPageContext()
    val auth = rememberAuthStore()
    val role = auth.currentUser?.role
    val allowed = SettingsCatalog.visibleTo(role).map { it.id }.toSet()
    val sections = settingsRegistry.filter { it.id in allowed }
    val attention = rememberSetupStatusStore().state.needsAttention(auth.isAdmin)

    val urlTab = ctx.route.params["tab"]?.takeIf { it.isNotBlank() }
    val resolved = SettingsCatalog.resolve(urlTab, sections.map { it.spec })

    var activeId by remember { mutableStateOf(resolved) }
    var searchOpen by remember { mutableStateOf(false) }
    val mounted = remember { mutableStateMapOf<String, Boolean>() }

    LaunchedEffect(urlTab, resolved) {
        if (resolved != null && resolved != activeId) activeId = resolved
        if (resolved != null && urlTab != null && urlTab != resolved) {
            window.history.replaceState(null, "", "/app/settings/$resolved")
        }
    }
    val current = activeId ?: resolved
    SideEffect { current?.let { mounted[it] = true } }

    fun select(id: String) {
        if (id == current) return
        mounted[id] = true
        activeId = id
        window.history.pushState(null, "", "/app/settings/$id")
        window.scrollTo(0.0, 0.0)
    }

    fun pick(hit: SettingsHit) {
        searchOpen = false
        val changed = hit.page.id != current
        if (changed) select(hit.page.id)
        SettingsJump.to(hit.entry.anchor, delayMs = if (changed) JUMP_AFTER_SWITCH_MS else 0)
    }

    DisposableEffect(Unit) {
        val onKey: (Event) -> Unit = handler@{ event ->
            if (event !is KeyboardEvent) return@handler
            val target = event.target as? HTMLElement
            val typing = target != null && (target.tagName in TEXT_TAGS || target.isContentEditable)
            val shortcut = (event.key == "/" && !typing) || ((event.metaKey || event.ctrlKey) && event.key.lowercase() == "k")
            if (shortcut) {
                event.preventDefault()
                searchOpen = true
            }
        }
        document.addEventListener("keydown", onKey)
        onDispose { document.removeEventListener("keydown", onKey) }
    }

    if (sections.isEmpty() || current == null) {
        AppShell(ground = ShellGround.Canvas, contentMaxWidth = Layout.wide, centered = true) {}
        return@AuthGuard
    }

    CompositionLocalProvider(LocalSettingsNavigate provides ::select) {
    AppShell(
        ground = ShellGround.Canvas,
        contentMaxWidth = Layout.wide,
        centered = true,
        sidebar = {
            SettingsSidebar(
                sections = sections,
                activeId = current,
                attentionId = if (attention) "integrations" else null,
                onSelect = ::select,
                onSearch = { searchOpen = true },
            )
        },
    ) {
        sections.forEach { section ->
            if (mounted[section.id] == true) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            if (section.id != current) property("display", "none")
                        },
                ) {
                    section.content()
                }
            }
        }
    }

    }

    SettingsSearchDialog(
        open = searchOpen,
        visible = sections.map { it.spec },
        onDismiss = { searchOpen = false },
        onPick = ::pick,
    )
}

private val TEXT_TAGS = setOf("INPUT", "TEXTAREA", "SELECT")
private const val JUMP_AFTER_SWITCH_MS = 320
